/**
 * Blog archive filters, AJAX grid and featured carousel.
 *
 * @package HelloElementorChild
 */

import { initCamstReveal, refreshCamstReveal } from './camst-reveal.js';

var blogInstances = new WeakMap();
var blogRequestIds = new WeakMap();

function getBlogState(root, fallback) {
  return blogInstances.get(root) || fallback || { categorie: [], argomenti: [], page: 1 };
}

function setBlogState(root, state) {
  blogInstances.set(root, state);
}

function nextBlogRequestId(root) {
  var next = (blogRequestIds.get(root) || 0) + 1;
  blogRequestIds.set(root, next);
  return next;
}

function isLatestBlogRequest(root, requestId) {
  return blogRequestIds.get(root) === requestId;
}

function escapeHtml(value) {
  var div = document.createElement('div');
  div.textContent = value == null ? '' : String(value);
  return div.innerHTML;
}

function buildCardHtml(post) {
  var image = post.image
    ? '<img class="camst-blog-archive__card-image" src="' + escapeHtml(post.image) + '" alt="" loading="lazy" decoding="async">'
    : '';
  var tag = post.categoria
    ? '<span class="camst-blog-archive__card-tag">' + escapeHtml(post.categoria) + '</span>'
    : '';
  var topic = post.argomento
    ? '<span class="camst-blog-archive__card-topic">' + escapeHtml(post.argomento) + '</span>'
    : '';

  return (
    '<article class="camst-blog-archive__card">' +
      '<a class="camst-blog-archive__card-link" href="' + escapeHtml(post.url) + '">' +
        '<div class="camst-blog-archive__card-media">' + image + tag + '</div>' +
        '<div class="camst-blog-archive__card-body">' +
          topic +
          '<h3 class="camst-blog-archive__card-title">' + escapeHtml(post.title) + '</h3>' +
          '<p class="camst-blog-archive__card-excerpt">' + escapeHtml(post.excerpt) + '</p>' +
          '<div class="camst-blog-archive__card-footer">' +
            '<div class="camst-blog-archive__card-meta">' +
              '<span class="camst-blog-archive__card-type">' + escapeHtml(post.content_type) + '</span>' +
              '<span class="camst-blog-archive__card-date">' + escapeHtml(post.date) + '</span>' +
            '</div>' +
            '<span class="camst-blog-archive__card-cta">Leggi di più <i class="fas fa-arrow-right" aria-hidden="true"></i></span>' +
          '</div>' +
        '</div>' +
      '</a>' +
    '</article>'
  );
}

function buildPaginationHtml(pagination) {
  var current = pagination.current || 1;
  var total = pagination.total || 1;

  if (total <= 1) {
    return '<nav class="camst-blog-archive__pagination" data-camst-blog-pagination aria-label="Paginazione blog" hidden></nav>';
  }

  var html = '<nav class="camst-blog-archive__pagination" data-camst-blog-pagination aria-label="Paginazione blog">';
  html += '<button type="button" class="camst-blog-archive__page-btn camst-blog-archive__page-btn--nav" data-page="' + Math.max(1, current - 1) + '"' + (current <= 1 ? ' disabled' : '') + ' aria-label="Pagina precedente"><i class="fas fa-chevron-left" aria-hidden="true"></i></button>';

  var pages = getPaginationWindow(current, total);

  pages.forEach(function (pageNumber) {
    if (pageNumber === '…') {
      html += '<span class="camst-blog-archive__page-ellipsis" aria-hidden="true">…</span>';
      return;
    }

    var isActive = pageNumber === current;
    html += '<button type="button" class="camst-blog-archive__page-btn' + (isActive ? ' is-active' : '') + '" data-page="' + pageNumber + '"' + (isActive ? ' aria-current="page"' : '') + '>' + pageNumber + '</button>';
  });

  html += '<button type="button" class="camst-blog-archive__page-btn camst-blog-archive__page-btn--nav" data-page="' + Math.min(total, current + 1) + '"' + (current >= total ? ' disabled' : '') + ' aria-label="Pagina successiva"><i class="fas fa-chevron-right" aria-hidden="true"></i></button>';
  html += '</nav>';

  return html;
}

function getPaginationWindow(current, total) {
  if (total <= 5) {
    return Array.from({ length: total }, function (_, index) {
      return index + 1;
    });
  }

  var pages = [1];

  if (current > 3) {
    pages.push('…');
  }

  for (var i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
    pages.push(i);
  }

  if (current < total - 2) {
    pages.push('…');
  }

  pages.push(total);

  return pages.filter(function (value, index, array) {
    return array.indexOf(value) === index;
  });
}

function getSelectedArgomenti(root) {
  return Array.prototype.slice
    .call(root.querySelectorAll('[data-camst-blog-dropdown-panel] input[type="checkbox"]:checked'))
    .map(function (input) {
      return input.value;
    });
}

function getParentCategoriesForSelectedArgomenti(root) {
  var parents = [];

  root.querySelectorAll('[data-camst-blog-dropdown-panel] input[type="checkbox"]:checked').forEach(function (input) {
    var parentSlug = input.getAttribute('data-categoria') || '';

    if (parentSlug && parents.indexOf(parentSlug) === -1) {
      parents.push(parentSlug);
    }
  });

  return parents;
}

function filterArgomentiForCategorie(root, categorie, argomenti) {
  if (!Array.isArray(argomenti) || !argomenti.length) {
    return [];
  }

  if (!Array.isArray(categorie) || !categorie.length) {
    return argomenti.slice();
  }

  return argomenti.filter(function (slug) {
    var input = root.querySelector('[data-camst-blog-dropdown-panel] input[type="checkbox"][value="' + slug + '"]');

    if (!input) {
      return false;
    }

    var parentSlug = input.getAttribute('data-categoria') || '';

    return parentSlug && categorie.indexOf(parentSlug) !== -1;
  });
}

function syncArgomentoCheckboxes(root, argomenti) {
  root.querySelectorAll('[data-camst-blog-dropdown-panel] input[type="checkbox"]').forEach(function (input) {
    input.checked = argomenti.indexOf(input.value) !== -1;
  });
}

function updateDropdownLabel(root, count) {
  var label = root.querySelector('[data-camst-blog-dropdown-label]');

  if (!label) {
    return;
  }

  label.textContent = count > 0 ? 'Argomenti (' + count + ')' : 'Filtra per Argomento';
}

var BLOG_DROPDOWN_ANIM_MS = 420;

function clearBlogDropdownCloseTimer(root) {
  if (!root._camstDropdownCloseTimer) {
    return;
  }

  clearTimeout(root._camstDropdownCloseTimer);
  root._camstDropdownCloseTimer = null;
}

function finalizeBlogDropdownClose(root, panel) {
  clearBlogDropdownCloseTimer(root);

  if (root.classList.contains('is-dropdown-open')) {
    return;
  }

  panel.setAttribute('hidden', 'hidden');
}

function setBlogDropdownOpen(root, isOpen) {
  var panel = root.querySelector('[data-camst-blog-dropdown-panel]');
  var toggle = root.querySelector('[data-camst-blog-dropdown-toggle]');

  if (!panel || !toggle) {
    return;
  }

  clearBlogDropdownCloseTimer(root);

  if (panel._camstDropdownTransitionHandler) {
    panel.removeEventListener('transitionend', panel._camstDropdownTransitionHandler);
    panel._camstDropdownTransitionHandler = null;
  }

  if (isOpen) {
    panel.removeAttribute('hidden');
    toggle.setAttribute('aria-expanded', 'true');
    document.body.classList.add('camst-blog-dropdown-open');

    // Due frame per far partire la transizione CSS dopo il reflow.
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        root.classList.add('is-dropdown-open');
      });
    });
    return;
  }

  toggle.setAttribute('aria-expanded', 'false');
  root.classList.remove('is-dropdown-open');
  document.body.classList.remove('camst-blog-dropdown-open');

  panel._camstDropdownTransitionHandler = function (event) {
    if (event.target !== panel || event.propertyName !== 'transform') {
      return;
    }

    panel.removeEventListener('transitionend', panel._camstDropdownTransitionHandler);
    panel._camstDropdownTransitionHandler = null;
    finalizeBlogDropdownClose(root, panel);
  };

  panel.addEventListener('transitionend', panel._camstDropdownTransitionHandler);

  root._camstDropdownCloseTimer = setTimeout(function () {
    finalizeBlogDropdownClose(root, panel);
  }, BLOG_DROPDOWN_ANIM_MS);
}

function closeBlogDropdown(root) {
  setBlogDropdownOpen(root, false);
}

function updateCategoryPills(root, categorie) {
  root.querySelectorAll('[data-camst-blog-categoria]').forEach(function (button) {
    var slug = button.getAttribute('data-camst-blog-categoria') || '';
    var isActive = slug === '' ? categorie.length === 0 : categorie.indexOf(slug) !== -1;
    button.classList.toggle('is-active', isActive);
  });
}

function scrollToFilters(root) {
  var filters = root.querySelector('[data-camst-blog-filters]');

  if (!filters) {
    return;
  }

  filters.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function fetchPosts(root, state, options) {
  var opts = options || {};
  var requestId = nextBlogRequestId(root);
  var restUrl = root.getAttribute('data-rest-url');
  var perPage = root.getAttribute('data-per-page') || '6';
  var params = new URLSearchParams({
    page: String(state.page || 1),
    per_page: perPage,
  });

  if (Array.isArray(state.categorie) && state.categorie.length) {
    params.set('categoria', state.categorie.join(','));
  }

  if (Array.isArray(state.argomenti) && state.argomenti.length) {
    params.set('argomento', state.argomenti.join(','));
  }

  updateCategoryPills(root, state.categorie || []);
  updateDropdownLabel(root, (state.argomenti || []).length);
  setBlogState(root, state);
  root.classList.add('is-loading');

  return fetch(restUrl + '?' + params.toString(), {
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
    },
  })
    .then(function (response) {
      if (!response.ok) {
        throw new Error('Blog request failed');
      }

      return response.json();
    })
    .then(function (data) {
      if (!isLatestBlogRequest(root, requestId)) {
        return;
      }

      var grid = root.querySelector('[data-camst-blog-grid]');
      var paginationWrap = root.querySelector('[data-camst-blog-pagination]');

      if (grid) {
        grid.innerHTML = (data.posts || []).map(buildCardHtml).join('');
      }

      if (paginationWrap && paginationWrap.parentNode) {
        paginationWrap.outerHTML = buildPaginationHtml(data.pagination || { current: 1, total: 1 });
      }

      if (data.url) {
        window.history.pushState(state, '', data.url);
      }

      if (data.filters) {
        state.categorie = Array.isArray(data.filters.categorie) ? data.filters.categorie : [];
        state.argomenti = Array.isArray(data.filters.argomenti) ? data.filters.argomenti : [];
      }

      updateCategoryPills(root, state.categorie || []);
      updateDropdownLabel(root, (state.argomenti || []).length);
      setBlogState(root, state);
      bindPagination(root, state);

      if (opts.scroll) {
        scrollToFilters(root);
      }
    })
    .catch(function () {
      if (!isLatestBlogRequest(root, requestId)) {
        return;
      }

      updateCategoryPills(root, state.categorie || []);
      updateDropdownLabel(root, (state.argomenti || []).length);
    })
    .finally(function () {
      if (isLatestBlogRequest(root, requestId)) {
        root.classList.remove('is-loading');
      }
    });
}

function bindPagination(root, state) {
  var pagination = root.querySelector('[data-camst-blog-pagination]');

  if (!pagination) {
    return;
  }

  pagination.addEventListener('click', function (event) {
    var button = event.target.closest('[data-page]');

    if (!button || button.disabled) {
      return;
    }

    var currentState = getBlogState(root, state);
    var nextState = Object.assign({}, currentState, {
      page: parseInt(button.getAttribute('data-page'), 10) || 1,
    });

    fetchPosts(root, nextState, { scroll: true });
  });
}

function bindFeaturedCarousel(root) {
  var track = root.querySelector('[data-camst-featured-track]');

  if (!track) {
    return;
  }

  var prev = root.querySelector('[data-camst-featured-prev]');
  var next = root.querySelector('[data-camst-featured-next]');

  function scrollBySlide(direction) {
    track.scrollBy({
      left: direction * track.clientWidth,
      behavior: 'smooth',
    });
  }

  if (prev) {
    prev.addEventListener('click', function () {
      scrollBySlide(-1);
    });
  }

  if (next) {
    next.addEventListener('click', function () {
      scrollBySlide(1);
    });
  }
}

function toggleCategoria(categorie, slug) {
  if (slug === '') {
    return [];
  }

  var next = categorie.slice();
  var index = next.indexOf(slug);

  if (index === -1) {
    next.push(slug);
  } else {
    next.splice(index, 1);
  }

  return next;
}

function handleCategoryPillClick(root, initialState, button) {
  var currentState = getBlogState(root, initialState);
  var slug = button.getAttribute('data-camst-blog-categoria') || '';
  var nextState;

  if (slug === '') {
    root.querySelectorAll('[data-camst-blog-dropdown-panel] input[type="checkbox"]').forEach(function (input) {
      input.checked = false;
    });

    nextState = {
      categorie: [],
      argomenti: [],
      page: 1,
    };
  } else {
    var nextCategorie = toggleCategoria(currentState.categorie || [], slug);
    var nextArgomenti = filterArgomentiForCategorie(root, nextCategorie, currentState.argomenti || []);

    syncArgomentoCheckboxes(root, nextArgomenti);

    nextState = {
      categorie: nextCategorie,
      argomenti: nextArgomenti,
      page: 1,
    };
  }

  if (typeof button.blur === 'function') {
    button.blur();
  }

  fetchPosts(root, nextState, { scroll: true });
}

function bindFilters(root, state) {
  if (root._camstBlogFilterHandler) {
    return;
  }

  root._camstBlogFilterHandler = function (event) {
    var pill = event.target.closest('[data-camst-blog-categoria]');

    if (!pill || !root.contains(pill)) {
      return;
    }

    handleCategoryPillClick(root, state, pill);
  };

  root.addEventListener('click', root._camstBlogFilterHandler);

  var dropdown = root.querySelector('[data-camst-blog-dropdown]');
  var toggle = root.querySelector('[data-camst-blog-dropdown-toggle]');
  var panel = root.querySelector('[data-camst-blog-dropdown-panel]');
  var backdrop = root.querySelector('[data-camst-blog-dropdown-backdrop]');
  var mobileClose = root.querySelector('[data-camst-blog-dropdown-close]');
  var apply = root.querySelector('[data-camst-blog-apply]');
  var clear = root.querySelector('[data-camst-blog-clear]');

  if (toggle && panel) {
    toggle.addEventListener('click', function () {
      setBlogDropdownOpen(root, panel.hasAttribute('hidden'));
    });

    if (backdrop) {
      backdrop.addEventListener('click', function () {
        closeBlogDropdown(root);
      });
    }

    if (mobileClose) {
      mobileClose.addEventListener('click', function () {
        closeBlogDropdown(root);
      });
    }

    document.addEventListener('click', function (event) {
      if (!dropdown || dropdown.contains(event.target)) {
        return;
      }

      closeBlogDropdown(root);
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        closeBlogDropdown(root);
      }
    });
  }

  if (apply) {
    apply.addEventListener('click', function () {
      var currentState = getBlogState(root, state);
      var selectedArgomenti = getSelectedArgomenti(root);
      var parentCategorie = getParentCategoriesForSelectedArgomenti(root);
      var nextState = {
        categorie: selectedArgomenti.length
          ? parentCategorie
          : (currentState.categorie || []),
        argomenti: selectedArgomenti,
        page: 1,
      };

      closeBlogDropdown(root);

      fetchPosts(root, nextState, { scroll: true });
    });
  }

  if (clear) {
    clear.addEventListener('click', function () {
      root.querySelectorAll('[data-camst-blog-dropdown-panel] input[type="checkbox"]').forEach(function (input) {
        input.checked = false;
      });

      var currentState = getBlogState(root, state);
      var nextState = {
        categorie: currentState.categorie || [],
        argomenti: [],
        page: 1,
      };

      fetchPosts(root, nextState, { scroll: true });
    });
  }
}

export function initCamstBlogArchive(scope) {
  var roots = [];

  if (!scope) {
    roots = Array.prototype.slice.call(document.querySelectorAll('[data-camst-blog-archive]'));
  } else if (scope.matches && scope.matches('[data-camst-blog-archive]')) {
    roots = [scope];
  } else {
    roots = Array.prototype.slice.call(scope.querySelectorAll('[data-camst-blog-archive]'));
  }

  roots.forEach(function (root) {
    if (root.dataset.camstBlogArchiveInit === '1') {
      return;
    }

    root.dataset.camstBlogArchiveInit = '1';

    var state = {
      categorie: (root.getAttribute('data-initial-categorie') || '')
        .split(',')
        .filter(Boolean),
      argomenti: (root.getAttribute('data-initial-argomento') || '')
        .split(',')
        .filter(Boolean),
      page: parseInt(root.getAttribute('data-initial-page') || '1', 10) || 1,
    };

    initCamstReveal(root.querySelector('[data-camst-reveal-root]') || root);
    bindFeaturedCarousel(root);
    bindFilters(root, state);
    bindPagination(root, state);

    window.addEventListener('popstate', function () {
      window.location.reload();
    });

    setBlogState(root, state);
  });
}

export function teardownCamstBlogArchive(scope) {
  if (!scope) {
    return;
  }

  var roots = scope.matches && scope.matches('[data-camst-blog-archive]')
    ? [scope]
    : Array.prototype.slice.call(scope.querySelectorAll('[data-camst-blog-archive]'));

  roots.forEach(function (root) {
    if (root._camstBlogFilterHandler) {
      root.removeEventListener('click', root._camstBlogFilterHandler);
      delete root._camstBlogFilterHandler;
    }

    closeBlogDropdown(root);
    delete root.dataset.camstBlogArchiveInit;
    blogInstances.delete(root);
    blogRequestIds.delete(root);
  });
}

export function refreshCamstBlogArchive() {
  refreshCamstReveal();
}
